import '../../styles/restScreens.css';
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import DashboardGrid from '../widgets/DashboardGrid';
import WidgetLibrary from '../widgets/WidgetLibrary';
import WidgetFactory from '../widgets/WidgetFactory';
import WidgetConfigModal from '../widgets/WidgetConfigModal';
import { DASHBOARD_TEMPLATES, createWidgetInstance } from '../widgets/widgetRegistry';
import { supabaseClient } from '../../config/supabase';
import { fetchDashboardMetrics } from '../../lib/dashboardMetrics';
import useModuleModes from '../../hooks/useModuleModes';
import { loadReportIncludeTest, setReportIncludeTest } from '../../lib/breakerPanel';
import { useSupportSession, SupportWriteGuard } from '../../context/SupportSessionContext';
import CommsGlancePanel from './CommsGlancePanel';
import DashboardGoalsPanel from './DashboardGoalsPanel';
import HoverHelp from '../shared/HoverHelp';
import ActionPillMenu from '../ui/ActionPillMenu';
import useModuleAccess from '../../hooks/useModuleAccess';
import useTripPriceAccess from '../../hooks/useTripPriceAccess';
import { filterDashboardWidgets, getWidgetDefinition, isWidgetAllowed } from '../widgets/widgetRegistry';

const DATE_RANGE_LABELS = {
  today: 'Today',
  week: 'This Week',
  '30days': 'Past 30 Days',
  month: 'This Month',
  ytd: 'Year to Date',
  custom: 'Custom Range',
};

const QUICK_ACTION_WIDGETS = [
  { type: 'kpi_card', icon: '📊', name: 'KPI Card', desc: 'Display key metrics and KPIs' },
  { type: 'revenue_chart', icon: '💰', name: 'Revenue Chart', desc: 'Track revenue trends over time' },
  { type: 'recent_activity', icon: '📋', name: 'Recent Activity', desc: 'Show latest updates and changes' },
];

const TEMPLATES_ICON = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 13a1 1 0 011-1h4a1 1 0 011 1v6a1 1 0 01-1 1h-4a1 0 01-1-1v-6z" />
  </svg>
);

const QUICK_ACTIONS_ICON = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

/**
 * CustomizableDashboard Component
 *
 * Main dashboard component that manages widget state, layout, and persistence
 */
const CustomizableDashboard = ({ userId, theme = 'light', triggerAutosave = () => {}, onAction, onReady, isUser = false }) => {
  const [widgets, setWidgets] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [configWidget, setConfigWidget] = useState(null);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [configModalMode, setConfigModalMode] = useState('edit'); // 'edit' or 'create'
  const [pendingWidgetType, setPendingWidgetType] = useState(null); // For creating new widgets
  const [isInitialLoading, setIsInitialLoading] = useState(true); // True while the saved layout is being fetched

  // BL-194 Gate 1: one fetch of the Supabase-direct tile aggregates, shared by
  // every data-backed tile on the board. Tiles no longer each invent their own
  // numbers, and no tile issues its own query.
  const [metrics, setMetrics] = useState(null);
  const { allowed: canViewFinancial, loading: financialAccessLoading } = useModuleAccess(
    isUser ? 'financial' : null,
    'view'
  );
  const canShowTripPrices = useTripPriceAccess(isUser);
  const { allowed: canViewCrm } = useModuleAccess(isUser ? 'crm' : null, 'view');
  const { allowed: canViewDispatch } = useModuleAccess(isUser ? 'dispatch' : null, 'view');
  const { allowed: canViewTasks } = useModuleAccess(isUser ? 'tasks' : null, 'view');
  const { allowed: canViewComms } = useModuleAccess(isUser ? 'comms' : null, 'view');
  const { allowed: canViewSettings } = useModuleAccess(isUser ? 'settings' : null, 'view');
  const { allowed: canViewReports } = useModuleAccess(isUser ? 'reports' : null, 'view');
  const areaAccess = useMemo(() => ({
    financial: !isUser || canViewFinancial,
    crm: !isUser || canViewCrm,
    dispatch: !isUser || canViewDispatch,
    tasks: !isUser || canViewTasks,
    comms: !isUser || canViewComms,
    settings: !isUser || canViewSettings,
    reports: !isUser || canViewReports,
  }), [isUser, canViewFinancial, canViewCrm, canViewDispatch, canViewTasks,
    canViewComms, canViewSettings, canViewReports]);
  const visibleWidgets = filterDashboardWidgets(widgets, areaAccess);

  // Date Range State
  const [dateRange, setDateRange] = useState('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Load a dashboard template
  const loadTemplate = useCallback((templateId) => {
    const template = DASHBOARD_TEMPLATES[templateId.toUpperCase()];
    if (!template) return;

    const templateWidgets = template.widgets.map(widgetConfig =>
      createWidgetInstance(widgetConfig.type, widgetConfig)
    );

    setWidgets(templateWidgets);
  }, []);

  const loadDashboardLayout = useCallback(async () => {
    try {
      const { data, error } = await supabaseClient
        .from('dashboard_layouts')
        .select('layout')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading dashboard layout:', error);
        return;
      }

      if (data && data.layout) {
        setWidgets(data.layout);
        setLastSaved(new Date());
      } else {
        // Load default template if no saved layout
        loadTemplate('executive');
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
      // Load default template on error
      loadTemplate('executive');
    } finally {
      // Fetch is done (success or failure) - stop showing the loading state so the
      // real widgets (or the genuine empty state) can display accurately.
      setIsInitialLoading(false);
    }
  }, [loadTemplate, userId]);

  // Load dashboard layout from Supabase
  useEffect(() => {
    if (userId) {
      loadDashboardLayout();
    }
  }, [userId, loadDashboardLayout]);

  // Tile aggregates. RLS scopes every view to the caller's tenant. Failed
  // sources retain their widget-level empty state without raising a page error.
  // Breaker Panel fix round 1 (finding 15): once a module is Live, its TEST records leave the tiles, KPIs and report views
  // (the database filters the views per person). "Include TEST" brings them back for an audit; the choice is kept per person.
  const moduleModes = useModuleModes();
  const anyModuleLive = Boolean(moduleModes && Object.values(moduleModes).some((m) => m?.mode === 'live'));
  const dispatchLive = moduleModes?.dispatch?.mode === 'live';
  const [includeTest, setIncludeTest] = useState(false);
  const [includeTestBusy, setIncludeTestBusy] = useState(false);
  useEffect(() => {
    let cancelled = false;
    loadReportIncludeTest(supabaseClient).then((value) => { if (!cancelled) setIncludeTest(value); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const toggleIncludeTest = useCallback(async () => {
    setIncludeTestBusy(true);
    try {
      const next = await setReportIncludeTest(supabaseClient, !includeTest);
      setIncludeTest(Boolean(next));
    } catch (error) {
      /* the choice stays as it was */
    } finally {
      setIncludeTestBusy(false);
    }
  }, [includeTest]);

  useEffect(() => {
    let cancelled = false;

    async function loadMetrics() {
      if (isUser && financialAccessLoading) return;
      if (isUser && !canViewFinancial) {
        setMetrics({});
        return;
      }
      try {
        const result = await fetchDashboardMetrics({
          timeframe: '90d',
          showTripPrices: canShowTripPrices,
          excludeTestTrips: dispatchLive && !includeTest,
        });
        if (cancelled) return;
        setMetrics(result);
      } catch (error) {
        if (cancelled) return;
        setMetrics({ errors: [{ source: 'dashboard', message: error.message }] });
      }
    }

    loadMetrics();
    return () => { cancelled = true; };
  }, [canShowTripPrices, canViewFinancial, financialAccessLoading, isUser, dispatchLive, includeTest]);

  // BL-402: the welcome sequence waits for the board's first data (layout + tile numbers, or their errors).
  const firstDataSettled = !isInitialLoading && metrics !== null;
  useEffect(() => {
    if (firstDataSettled) onReady?.();
  }, [firstDataSettled, onReady]);

  const saveDashboardLayout = useCallback(async (layoutToSave = widgets) => {
    if (!userId) return;

    setIsSaving(true);
    try {
      const { error } = await supabaseClient
        .from('dashboard_layouts')
        .upsert({
          user_id: userId,
          layout: layoutToSave,
          updated_at: new Date().toISOString()
        });

      if (error) {
        console.error('Error saving dashboard layout:', error);
      } else {
        setLastSaved(new Date());
        triggerAutosave('dashboard.layout');
      }
    } catch (error) {
      console.error('Error saving dashboard:', error);
    } finally {
      setIsSaving(false);
    }
  }, [triggerAutosave, userId, widgets]);

  // Handle layout change (drag/resize)
  const handleLayoutChange = useCallback((updatedWidgets) => {
    setWidgets(updatedWidgets);
  }, []);

  // Handle adding a new widget
  const handleAddWidget = useCallback((widgetInstance) => {
    setWidgets(prev => [...prev, widgetInstance]);
    setIsLibraryOpen(false);
  }, []);

  // Handle removing a widget
  const handleRemoveWidget = useCallback((widgetId) => {
    setWidgets(prev => prev.filter(w => w.id !== widgetId));
  }, []);

  // Handle widget configuration
  const handleConfigureWidget = useCallback((widgetId) => {
    const widget = widgets.find(w => w.id === widgetId);
    if (widget) {
      setConfigWidget(widget);
      setConfigModalMode('edit');
      setIsConfigModalOpen(true);
    }
  }, [widgets]);

  // Handle opening config modal for creating new widget
  const handleCreateWidget = useCallback((widgetType) => {
    const tempWidget = { type: widgetType, config: {}, settings: {} };
    setConfigWidget(tempWidget);
    setPendingWidgetType(widgetType);
    setConfigModalMode('create');
    setIsConfigModalOpen(true);
  }, []);

  // Handle saving widget configuration
  const handleSaveWidgetConfig = useCallback((updatedWidget) => {
    if (configModalMode === 'create') {
      // Creating a new widget - generate a complete widget instance
      const widgetInstance = createWidgetInstance(updatedWidget.type, {
        x: 0,
        y: Infinity, // Will be placed at bottom
        w: 4,
        h: 3
      });

      // Merge config and settings from modal
      widgetInstance.config = {
        ...widgetInstance.config,
        ...updatedWidget.config
      };
      widgetInstance.settings = updatedWidget.settings || {};

      // Add to widgets
      setWidgets(prev => [...prev, widgetInstance]);
    } else {
      // Editing existing widget
      setWidgets(prev => prev.map(w =>
        w.id === updatedWidget.id ? updatedWidget : w
      ));
    }

    setConfigWidget(null);
    setIsConfigModalOpen(false);
    setConfigModalMode('edit');
    setPendingWidgetType(null);
  }, [configModalMode]);

  // Handle grouping widgets together (iPhone-style folder) - supports multi-widget stacking
  const handleGroupWidgets = useCallback((widgetId1, widgetId2) => {
    const widget1 = widgets.find(w => w.id === widgetId1);
    const widget2 = widgets.find(w => w.id === widgetId2);

    if (!widget1 || !widget2) return;

    // Check if either widget is already a group
    const isWidget1Group = widget1.type === 'group';
    const isWidget2Group = widget2.type === 'group';

    if (isWidget1Group && isWidget2Group) {
      // Merge two groups - combine all widgets from both groups
      const group1Widgets = widget1.config.groupedWidgets || [];
      const group2Widgets = widget2.config.groupedWidgets || [];

      const mergedGroup = {
        ...widget1,
        config: {
          ...widget1.config,
          groupedWidgets: [...group1Widgets, ...group2Widgets]
        }
      };

      setWidgets(prev => [
        ...prev.filter(w => w.id !== widgetId1 && w.id !== widgetId2),
        mergedGroup
      ]);
    } else if (isWidget1Group) {
      // Add widget2 to widget1's group
      const updatedGroup = {
        ...widget1,
        config: {
          ...widget1.config,
          groupedWidgets: [...(widget1.config.groupedWidgets || []), widget2]
        }
      };

      setWidgets(prev => [
        ...prev.filter(w => w.id !== widgetId1 && w.id !== widgetId2),
        updatedGroup
      ]);
    } else if (isWidget2Group) {
      // Add widget1 to widget2's group
      const updatedGroup = {
        ...widget2,
        config: {
          ...widget2.config,
          groupedWidgets: [...(widget2.config.groupedWidgets || []), widget1]
        }
      };

      setWidgets(prev => [
        ...prev.filter(w => w.id !== widgetId1 && w.id !== widgetId2),
        updatedGroup
      ]);
    } else {
      // Create a new group with two widgets
      const groupWidget = createWidgetInstance('group', {
        x: Math.min(widget1.x, widget2.x),
        y: Math.min(widget1.y, widget2.y),
        w: 4,
        h: 4
      });

      // Set the grouped widgets in the config
      groupWidget.config = {
        ...groupWidget.config,
        title: 'Widget Group',
        groupedWidgets: [widget1, widget2],
        expanded: false
      };

      // Remove the individual widgets and add the group
      setWidgets(prev => [
        ...prev.filter(w => w.id !== widgetId1 && w.id !== widgetId2),
        groupWidget
      ]);
    }
  }, [widgets]);

  // Handle ungrouping a widget group
  const handleUngroup = useCallback((groupWidgetId) => {
    const groupWidget = widgets.find(w => w.id === groupWidgetId);
    if (!groupWidget || groupWidget.type !== 'group') return;

    const groupedWidgets = groupWidget.config.groupedWidgets || [];

    // Restore individual widgets with updated positions
    const restoredWidgets = groupedWidgets.map((widget, index) => ({
      ...widget,
      x: groupWidget.x + (index % 2) * 3,
      y: groupWidget.y + Math.floor(index / 2) * 3
    }));

    // Remove the group and add back individual widgets
    setWidgets(prev => [
      ...prev.filter(w => w.id !== groupWidgetId),
      ...restoredWidgets
    ]);
  }, [widgets]);

  // Handle removing a single widget from a group
  const handleRemoveFromGroup = useCallback((groupWidgetId, widgetIdToRemove) => {
    setWidgets(prev => prev.map(w => {
      if (w.id !== groupWidgetId || w.type !== 'group') return w;

      const updatedGroupedWidgets = (w.config.groupedWidgets || []).filter(
        gw => gw.id !== widgetIdToRemove
      );

      // If only one widget left, ungroup automatically
      if (updatedGroupedWidgets.length === 1) {
        // Restore the last widget
        const lastWidget = {
          ...updatedGroupedWidgets[0],
          x: w.x,
          y: w.y
        };

        // Schedule ungrouping for next render
        setTimeout(() => {
          setWidgets(current => [
            ...current.filter(cw => cw.id !== groupWidgetId),
            lastWidget
          ]);
        }, 0);

        return w; // Return unchanged for this render
      }

      // Update the group with remaining widgets
      return {
        ...w,
        config: {
          ...w.config,
          groupedWidgets: updatedGroupedWidgets
        }
      };
    }));
  }, []);

  // Toggle editing mode
  const toggleEditing = useCallback(() => {
    if (isEditing) {
      // Save when exiting editing mode
      saveDashboardLayout();
    }
    setIsEditing(!isEditing);
  }, [isEditing, saveDashboardLayout]);

  // Reset to default template
  const handleReset = useCallback(() => {
    if (window.confirm('Are you sure you want to reset your dashboard to the default template? This cannot be undone.')) {
      loadTemplate('executive');
      saveDashboardLayout();
    }
  }, [loadTemplate, saveDashboardLayout]);

  // Render a widget
  const renderWidget = useCallback((widget, { onRemove, onConfigure, isEditing }) => {
    return (
      <WidgetFactory
        widget={widget}
        onRemove={onRemove}
        onConfigure={onConfigure}
        isEditing={isEditing}
        onAction={onAction}
        onUngroup={handleUngroup}
        onRemoveFromGroup={handleRemoveFromGroup}
        theme={theme}
        metrics={metrics}
        isUser={isUser}
        areaAccess={areaAccess}
      />
    );
  }, [handleUngroup, handleRemoveFromGroup, theme, onAction, metrics, isUser, areaAccess]);

  return (
    <div data-m6-layout="dashboard" data-m6-component="CustomizableDashboard" className="h-full flex flex-col">
      {/* Dashboard Toolbar */}
      <div className={`flex items-center justify-between px-6 py-4 border-b ${
        'bg-[var(--surface-raised)] border-[var(--surface-border)]'
      }`}>
        <div>
          <h1 className={'text-2xl font-bold text-[var(--ink)]'}>
            My Dashboard
          </h1>
          {lastSaved && (
            <p className={'text-xs mt-1 text-[var(--accent-ink)]'}>
              Last saved: {lastSaved.toLocaleTimeString()}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Date Range Selector (hidden while editing to reduce toolbar clutter) */}
          {!isEditing && (
            <>
              {/* BL-177 Gate 17d: an Action Pill menu — one pill per range. */}
              <div className="relative inline-flex items-center">
                <ActionPillMenu
                  menuLabel="Date range"
                  placement="bottom"
                  align="end"
                  options={Object.entries(DATE_RANGE_LABELS).map(([range, label]) => ({
                    key: range,
                    label,
                    active: dateRange === range,
                    onSelect: () => setDateRange(range),
                  }))}
                  triggerClassName={'px-4 py-2 text-sm font-medium rounded-lg border transition-colors inline-flex items-center gap-2 text-[var(--ink)] bg-[var(--surface-raised)] border-[var(--surface-border)] hover:bg-[var(--surface-sunken)]'}
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="whitespace-nowrap">{dateRange === 'custom' ? 'Custom' : (DATE_RANGE_LABELS[dateRange] || 'Select Range')}</span>
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </ActionPillMenu>
                <HoverHelp helpId="dashboard.date-range" userId={userId} />
              </div>

              {/* Custom range dates sit beside the pill (a pill can't hold a form). */}
              {dateRange === 'custom' && (
                <div className="inline-flex items-center gap-2 text-xs text-[var(--ink-muted)]">
                  <label className="inline-flex items-center gap-1">
                    From:
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      className="px-2 py-1.5 text-sm rounded border bg-[var(--surface-sunken)] border-[var(--surface-border)] text-[var(--ink)]"
                    />
                  </label>
                  <label className="inline-flex items-center gap-1">
                    To:
                    <input
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      className="px-2 py-1.5 text-sm rounded border bg-[var(--surface-sunken)] border-[var(--surface-border)] text-[var(--ink)]"
                    />
                  </label>
                </div>
              )}

              {anyModuleLive && (
                <button
                  type="button"
                  role="switch"
                  aria-checked={includeTest}
                  disabled={includeTestBusy}
                  onClick={toggleIncludeTest}
                  data-testid="dashboard-include-test"
                  title="Live modules leave TEST records out of the dashboard. Turn on to include them (for an audit)."
                  className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors inline-flex items-center gap-2 text-[var(--ink)] border-[var(--surface-border)] ${includeTest ? 'bg-[#FEF9C3] dark:bg-[rgba(250,204,21,.12)]' : 'bg-[var(--surface-raised)] hover:bg-[var(--surface-sunken)]'}`}
                >
                  {includeTest ? 'Including TEST' : 'Include TEST'}
                </button>
              )}
            </>
          )}

          {/* Edit Dashboard Controls - Gated by SupportWriteGuard */}
          <SupportWriteGuard
            fallback={
              <span className="text-xs px-3 py-1.5 rounded-lg bg-[var(--surface-sunken)] text-[var(--ink-muted)] border border-[var(--surface-border)] flex items-center gap-1.5 font-medium">
                🔒 Read-only Support Mode
              </span>
            }
          >
            {!isEditing ? (
            <span className="inline-flex items-center">
              <button
                onClick={toggleEditing}
                className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors inline-flex items-center gap-2 ${
                  'text-[var(--ink)] bg-[var(--surface-raised)] border-[var(--surface-border)] hover:bg-[var(--surface-sunken)]'
                }`}
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <span className="whitespace-nowrap">Edit Dashboard</span>
              </button>
              <HoverHelp helpId="dashboard.edit-layout" userId={userId} />
            </span>
          ) : (
            <>
              {/* Configure — BL-177 Gate 17d Action Pill menu; Templates and Quick Actions open
                  their own pill menus to the side. */}
              <div className="relative inline-flex items-center">
                <ActionPillMenu
                  menuLabel="Configure dashboard"
                  placement="bottom"
                  align="end"
                  options={[
                    {
                      key: 'formula_builder',
                      label: 'Custom Widget',
                      icon: '🧮',
                      title: 'Build custom formulas & KPIs',
                      onSelect: () => handleCreateWidget('formula_builder'),
                    },
                    {
                      key: 'templates',
                      label: 'Templates',
                      icon: TEMPLATES_ICON,
                      title: 'Load dashboard layouts',
                      placement: 'left',
                      options: Object.entries(DASHBOARD_TEMPLATES).map(([key, template]) => ({
                        key,
                        label: template.name,
                        title: template.description,
                        onSelect: () => loadTemplate(key.toLowerCase()),
                      })),
                    },
                    {
                      key: 'quick_actions',
                      label: 'Quick Actions',
                      icon: QUICK_ACTIONS_ICON,
                      title: 'Add widgets to dashboard',
                      placement: 'left',
                      options: QUICK_ACTION_WIDGETS.filter((widget) => isWidgetAllowed({ type: widget.type, config: getWidgetDefinition(widget.type)?.defaultConfig }, areaAccess)).map((widget) => ({
                        key: widget.type,
                        label: widget.name,
                        icon: widget.icon,
                        title: widget.desc,
                        onSelect: () => handleCreateWidget(widget.type),
                      })),
                    },
                  ]}
                  triggerClassName="px-4 py-2 text-sm font-medium rounded-lg transition-all inline-flex items-center gap-2 bg-[var(--btn-fill)] text-[var(--btn-ink)] hover:bg-[var(--btn-fill-hover)] hover:shadow-lg"
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="whitespace-nowrap">Configure</span>
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </ActionPillMenu>
                <HoverHelp helpId="dashboard.configure-widgets" userId={userId} />
              </div>

              {/* Cancel Button */}
              <button
                onClick={() => {
                  setIsEditing(false);
                  // Reload dashboard to discard changes
                  loadDashboardLayout();
                }}
                className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors inline-flex items-center gap-2 ${
                  'text-[var(--ink)] bg-[var(--surface-raised)] border-[var(--surface-border)] hover:bg-[var(--surface-sunken)]'
                }`}
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="whitespace-nowrap">Cancel</span>
              </button>

              {/* Save & Exit Button */}
              <button
                onClick={toggleEditing}
                className="px-4 py-2 text-sm font-medium rounded-lg transition-all inline-flex items-center gap-2 bg-[var(--btn-fill)] text-[var(--btn-ink)] hover:bg-[var(--btn-fill-hover)] hover:shadow-lg"
              >
                {isSaving ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Save & Exit</span>
                  </>
                )}
              </button>
            </>
          )}

          {/* Reset Button */}
          {isEditing && (
            <button
              onClick={handleReset}
              className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                'text-[var(--ui-alert)] bg-[var(--surface-raised)] border-[var(--ui-alert)]/50 hover:bg-[var(--ui-alert)]/10'
              }`}
            >
              Reset
            </button>
          )}
          </SupportWriteGuard>
        </div>
      </div>

      {/* Dashboard Grid */}
      <div className={'flex-1 overflow-auto p-6 flex justify-center bg-[var(--surface-canvas)]'}>
        <div className="w-full max-w-7xl">
          {isInitialLoading ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <svg className="w-10 h-10 animate-spin text-[var(--accent-ink)] mb-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className={'text-sm text-[var(--ink-muted)]'}>Loading your dashboard...</p>
            </div>
          ) : (
            <>
              <DashboardGrid
                widgets={visibleWidgets}
                onLayoutChange={handleLayoutChange}
                onWidgetRemove={handleRemoveWidget}
                onWidgetConfigure={handleConfigureWidget}
                onGroupWidgets={handleGroupWidgets}
                isEditing={isEditing}
                renderWidget={renderWidget}
              />
              <div className="mt-6">
                {/* Migration 258: Company / Team / Mine goals, shown to every staff member by what the database lets them read. */}
                <DashboardGoalsPanel showProgress={!isUser || canViewFinancial} />
              </div>
              <div className="mt-6">
                {(!isUser || canViewComms) && <CommsGlancePanel theme={theme} onOpenCommunications={() => onAction?.('open_communications')} />}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Widget Library Sidebar */}
      <WidgetLibrary
        isOpen={isLibraryOpen}
        onClose={() => setIsLibraryOpen(false)}
        onAddWidget={handleAddWidget}
        theme={theme}
        areaAccess={areaAccess}
      />

      {/* Widget Configuration Modal */}
      <WidgetConfigModal
        widget={configWidget}
        isOpen={isConfigModalOpen}
        onClose={() => {
          setIsConfigModalOpen(false);
          setConfigWidget(null);
          setConfigModalMode('edit');
          setPendingWidgetType(null);
        }}
        onSave={handleSaveWidgetConfig}
        mode={configModalMode}
        theme={theme}
      />
    </div>
  );
};

export default CustomizableDashboard;
